import classNames from 'clsx';
import React, { type ReactElement, memo } from 'react';
import { useSelector } from 'react-redux';
import { ENABLE_FULL_RESPONSIVE } from 'reactApp/appHelpers/configHelpers';
import { enabledPublicDownloadLimits } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import { AllDocumentsCategoryType } from 'reactApp/modules/allDocuments/allDocuments.types';
import { getPublicLimits } from 'reactApp/modules/public/public.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';
import { AlldocumentsItem } from 'reactApp/ui/DataListItemRow/components/RowFor/AlldocumentsItem/AlldocumentsItem';
import { FamilyItem } from 'reactApp/ui/DataListItemRow/components/RowFor/Family';
import { GalleryItem } from 'reactApp/ui/DataListItemRow/components/RowFor/Gallery';
import { IncomingPublic } from 'reactApp/ui/DataListItemRow/components/RowFor/IncomingPublic';
import { VigboAlbumsItem } from 'reactApp/ui/DataListItemRow/components/RowFor/VigboAlbum';

import { AlbumsItem } from './components/RowFor/Album';
import { ArchiveItem } from './components/RowFor/Archive';
import { AttachesItem } from './components/RowFor/Attach';
import { FavoritesItem } from './components/RowFor/Favorites';
import { FeedItem } from './components/RowFor/Feed';
import { HomeItem } from './components/RowFor/Home';
import { IncomingItem } from './components/RowFor/Incoming';
import { PublicItem } from './components/RowFor/Public';
import { QuotaCleanerItem } from './components/RowFor/QuotaCleaner';
import { SearchItem } from './components/RowFor/Search';
import { SharedItem } from './components/RowFor/Shared';
import { StockItem } from './components/RowFor/Stock';
import { TrashbinItem } from './components/RowFor/Trashbin';
import styles from './DataListItemRow.css';
import newStyles from './DataListItemRow.new.css';
import type { DataListItemRowProps, DataListItemRowPropsWithAuthor } from './DataListItemRow.types';

const ItemFacade = (props: DataListItemRowProps) => {
    // eslint-disable-next-line sonarjs/max-switch-cases
    switch (props.storage) {
        case EStorageType.archive:
            return <ArchiveItem {...props} />;
        case EStorageType.sharedIncoming:
            return <IncomingItem {...props} />;
        case EStorageType.home: {
            return <HomeItem {...props} />;
        }
        case EStorageType.albums: {
            return <AlbumsItem {...props} />;
        }
        case EStorageType.vigbo: {
            return <VigboAlbumsItem {...props} />;
        }
        case EStorageType.sharedLinks:
        case EStorageType.sharedAutodelete:
            return <SharedItem {...props} />;
        case EStorageType.public:
            return <PublicItem {...props} />;
        case EStorageType.favorites:
            return <FavoritesItem {...props} />;
        case EStorageType.stock:
            return <StockItem {...props} />;
        case EStorageType.search: {
            return <SearchItem {...props} />;
        }
        case EStorageType.feed: {
            return <FeedItem {...props} />;
        }
        case EStorageType.alldocuments: {
            if (props.currentCategory === AllDocumentsCategoryType.sharedWithYou) {
                return <IncomingPublic {...props} storage={EStorageType.incomingPublic} />;
            }
            return <AlldocumentsItem {...props} />; // doc
        }
        case EStorageType.gallery: {
            return <GalleryItem {...props} />;
        }
        case EStorageType.attaches:
            return <AttachesItem {...props} />;
        case EStorageType.trashbin: {
            return <TrashbinItem {...props} />;
        }
        case EStorageType.quotaCleaner: {
            return <QuotaCleanerItem {...props} />;
        }
        case EStorageType.incomingPublic: {
            return <IncomingPublic {...props} />;
        }
        case EStorageType.familyFolders: {
            return <FamilyItem {...props} />;
        }
    }
};

export const DataListItemRow = memo((props: DataListItemRowPropsWithAuthor): ReactElement => {
    const { isVKEmbedded, isSelected, isSelecting, isActive, deniedByRights, parentItemRef, weblinkUrl, storage } = props;

    // tempexp_17847-start
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const downloadLimits = useSelector((state: RootState) => getPublicLimits(state, weblinkUrl || ''));
    const downloadTotal = downloadLimits && downloadLimits.total;
    const downloadLeft = downloadLimits && downloadLimits.left;
    const downloadCount = downloadTotal - downloadLeft;
    const disabledRow = deniedByRights && storage === EStorageType.alldocuments;
    const showDownloadCounter =
        enabledPublicDownloadLimits &&
        (storage === EStorageType.home || storage === EStorageType.sharedLinks) &&
        !isPaidUser &&
        downloadCount > 0;
    // tempexp_17847-end

    return (
        <div
            ref={parentItemRef}
            className={classNames(newStyles.root, {
                [styles.root_vk_embedded]: isVKEmbedded,
                [newStyles.root_selected]: isSelected,
                [newStyles.root_active]: isActive,
                [newStyles.root_selecting]: isSelecting,
                [newStyles.root_disabled]: disabledRow,
                [styles.root_responsive]: ENABLE_FULL_RESPONSIVE,
                [styles.root_xl]:
                    (props.storage === EStorageType.sharedIncoming ||
                        props.storage === EStorageType.gallery ||
                        props.storage === EStorageType.search) &&
                    ENABLE_FULL_RESPONSIVE,
                [newStyles.root_limit]: showDownloadCounter,
            })}
        >
            <ItemFacade {...props} />
        </div>
    );
});

DataListItemRow.displayName = 'DataListItemRow';
DataListItemRow.whyDidYouRender = true;
